/**
 * Module dependencies.
 */

import { Button, Loading, Subtitle, Type } from 'src/components/core';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next/types';
import { Market } from 'src/types/markets';
import { TechnologyProps } from 'src/types/technologies';
import { fetchMarket } from 'src/api/markets/fetch-market';
import { fetchMarkets } from 'src/api/markets/fetch-markets';
import { fetchTechnologies } from 'src/api/technologies/fetch-technologies';
import { ifProp, theme } from 'styled-tools';
import { media, units } from '@untile/react-components';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useRouter } from 'next/router';
import Container from 'src/components/core/layout/container';
import Metatags from 'src/components/core/metatags';
import TechnologyCard from 'src/components/cards/technology-card';
import get from 'lodash/get';
import map from 'lodash/map';
import styled, { css } from 'styled-components';
import useMarket from 'src/api/markets/use-market';
import useTechnologies from 'src/api/technologies/use-technologies';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  data: Market | null;
  technologies: TechnologyProps[];
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ isLoading: boolean }>`
  padding: ${units(14)} 0 ${units(5)};
  position: relative;

  ${media.min('md')`
    padding: ${units(20)} 0 ${units(18)};
  `}

  ${ifProp(
    'isLoading',
    css`
      min-height: 100vh;
    `
  )}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '. title       .'
    '. description .'
    '. button      .'
    '. list        .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;

  ${media.min('md')`
    grid-template-areas:
      '. title       .      .'
      '. description button .'
      '. list        list   .';
    grid-template-columns: 1fr 8fr 2fr 1fr;
    grid-template-rows: repeat(3, max-content);
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;

  ${media.min('md')`
    margin-bottom: ${units(1)};
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Subtitle)`
  grid-area: description;

  ${media.max('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `ButtonWrapper` styled component.
 */

const ButtonWrapper = styled.div`
  grid-area: button;

  ${media.min('md')`
    align-self: flex-end;
    display: flex;
    justify-self: flex-end;
    margin-bottom: 7px;
  `}
`;

/**
 * `ListWrapper` styled component.
 */

const ListWrapper = styled.div`
  display: grid;
  grid-area: list;
  grid-gap: ${units(2.5)};
  grid-template-columns: 1fr;
  margin-top: 50px;

  ${media.min('ms')`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${media.min('md')`
    grid-gap: 25px;
    grid-template-columns: repeat(3, 1fr);
    margin-top: ${units(9)};
  `}
`;

/**
 * `MarketDetail` page.
 */

const MarketDetail: NextPage<Props> = props => {
  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const buttonUrl = routeResolver('contactUs');
  const { query } = useRouter();
  const {
    data: marketData,
    isLoading: isLoadingPage,
    isSuccess: isSuccessPage
  } = useMarket({
    initialData: props.data,
    slug: get(query, 'slug') as string
  });

  const {
    data: technologies,
    isLoading: isTechnologiesLoading,
    isSuccess: isTechnologiesSuccess
  } = useTechnologies({
    filterParams: { market: marketData?.id },
    initialData: props.technologies
  });

  const isLoading = isLoadingPage || isTechnologiesLoading;
  const isSuccess = isSuccessPage && isTechnologiesSuccess;
  const description = marketData?.description;
  const title = marketData?.title;
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  return (
    <>
      <Metatags
        description={description}
        pageTitle={title}
        title={title}
      />

      <Wrapper isLoading={isLoading}>
        <Loading active={isLoading} />

        {isSuccess && (
          <StyledContainer>
            <Grid>
              <Title {...animProps}>{title}</Title>

              <Description {...animProps}>{description}</Description>

              <ButtonWrapper {...animProps}>
                <Button
                  colorTheme={'secondary'}
                  href={buttonUrl}
                  size={'large'}
                >
                  {translate('common:actions.reachOutToUs')}
                </Button>
              </ButtonWrapper>

              <ListWrapper>
                {map(
                  technologies,
                  ({
                    bgColor,
                    id,
                    listDescription,
                    listImage,
                    slug,
                    title
                  }: TechnologyProps) => (
                    <div
                      data-scroll={'fade-in-up-small'}
                      data-scroll-animation-delay={'150'}
                      data-scroll-offset={'20%'}
                      key={id}
                    >
                      <TechnologyCard
                        bgColor={bgColor}
                        description={listDescription}
                        imageUrl={listImage}
                        slug={slug}
                        title={title}
                      />
                    </div>
                  )
                )}
              </ListWrapper>
            </Grid>
          </StyledContainer>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `getStaticPaths`.
 */

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const marketsByLocale = await Promise.all(
    locales.map(async locale => await fetchMarkets({ locale }))
  );

  const slugs = new Set<string>();
  marketsByLocale.flatMap(markets =>
    markets?.forEach(({ slug }) => slugs.add(slug))
  );

  const paths = Array.from(slugs).map(slug => ({
    params: { slug }
  }));

  return {
    fallback: true,
    paths
  };
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<Props> = async ({
  locale,
  params
}) => {
  const slug = get(params, 'slug') as string;
  const data = await fetchMarket({ locale, slug });
  const technologies = await fetchTechnologies({
    locale,
    params: {
      filter: {
        market: data?.id
      }
    }
  });

  if (!data) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      data,
      technologies
    },
    revalidate: 60
  };
};
/**
 * Export `MarketDetail` page.
 */

export default MarketDetail;
